import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useGetCurrentUser } from '@/entities/authentication/api/generated/api';
import type { FeatureFlags } from '@/entities/session/model/featureFlag';
import type { ReleaseFlags } from '@/entities/session/model/releaseFlag';
import { useSessionStore } from '@/entities/session/model/sessionStore';
import { setUmamiTenantContext, UMAMI_TENANT_PROPERTY } from '@/shared/lib/umami';
import { Spinner, useSkeletonMinDisplay } from '../../shared/ui';

type AuthProviderProps = {
  children: ReactNode;
};

/**
 * 認証プロバイダー（薄いアダプター）
 *
 * App層の責務:
 * - Cookie監視による認証状態の自動更新
 * - entities/session ストアとの同期
 * - ユーザー情報の取得とキャッシュ
 *
 * 重要: useCookiesフックを使用してCookieの変更を検知し、自動的に再レンダリングする
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [cookies] = useCookies(['auth_token']);
  const { setAuthenticated, setUser, setReleaseFlags, setFeatures } = useSessionStore();
  const [releaseFlagsLoaded, setReleaseFlagsLoaded] = useState(false);
  const [featureFlagsLoaded, setFeatureFlagsLoaded] = useState(false);

  // Cookieの存在を確認して認証状態を判定
  const isAuthenticated = Boolean(cookies.auth_token);

  // 認証状態をストアに同期
  useEffect(() => {
    setAuthenticated(isAuthenticated);
  }, [isAuthenticated, setAuthenticated]);

  // ログイン済みの場合のみユーザー情報を取得
  const {
    data: currentUser,
    isLoading: isLoadingUser,
    isError,
  } = useGetCurrentUser({
    query: {
      enabled: isAuthenticated,
      staleTime: 1000 * 60 * 60 * 1, // 1時間キャッシュ
    },
  });

  // ユーザー情報をストアに同期
  useEffect(() => {
    if (currentUser) {
      setUser({
        id: currentUser.id,
        name: currentUser.name,
        email: currentUser.email,
        lastLogin: currentUser.last_login ?? null,
        isFirstLoginCompleted: currentUser.is_first_login_completed,
        role: currentUser.role ?? null,
        tenant: {
          id: currentUser.tenant.id,
          code: currentUser.tenant.code,
          name: currentUser.tenant.name,
          localGovernmentCode: currentUser.tenant.local_government_code,
        },
      });
      // 全 Umami カスタムイベントへ自動付与するテナントコンテキストを注入する
      // （admin の分析画面で event_name × テナントの内訳集計に使う）
      setUmamiTenantContext({
        [UMAMI_TENANT_PROPERTY.ID]: currentUser.tenant.id,
        [UMAMI_TENANT_PROPERTY.CODE]: currentUser.tenant.code,
        [UMAMI_TENANT_PROPERTY.NAME]: currentUser.tenant.name,
      });
    }
  }, [currentUser, setUser]);

  // 未認証時はテナントコンテキストをクリアする（ログアウト・セッション失効）
  useEffect(() => {
    if (!isAuthenticated) {
      setUmamiTenantContext(null);
    }
  }, [isAuthenticated]);

  // Release Flags をストアに同期
  useEffect(() => {
    if (currentUser) {
      const releaseFlags = Object.fromEntries(
        (currentUser.release_flags ?? []).map((flag) => [flag.type, flag.is_enabled]),
      ) as ReleaseFlags;
      setReleaseFlags(releaseFlags);
      setReleaseFlagsLoaded(true);
    }
  }, [currentUser, setReleaseFlags]);

  // Feature Flags（テナント単位）をストアに同期
  useEffect(() => {
    if (currentUser) {
      // features は同じ /me レスポンスに含まれるため release_flags と同時にセットされる
      setFeatures((currentUser.features ?? {}) as FeatureFlags);
      setFeatureFlagsLoaded(true);
    }
  }, [currentUser, setFeatures]);

  const loading = useSkeletonMinDisplay(isLoadingUser);

  // tanstack router初期化前のため
  if (isError && window.location.pathname !== '/404') {
    window.location.replace('/404');
  }

  // release_flags / features がストアにセットされるまでローディングを続ける
  // useEffect は render 後に実行されるため、children（Router）の beforeLoad が
  // フラグセット前に走るレースコンディションを防ぐ。
  // 特に requireFeatureFlag ルートガードは features 未セット時に空 {} を hidden 扱いし
  // 誤って 404 リダイレクトしてしまうため、featureFlagsLoaded も待つ。
  if (loading || (isAuthenticated && (!releaseFlagsLoaded || !featureFlagsLoaded))) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }
  return <>{children}</>;
};
